import React, { useState, useMemo } from "react";
import { PageHeader } from "@finitless/shared/components";
import { useTranslation } from "react-i18next";
import { useDriverCompanies } from "../../../hooks/useDriverCompanies";
import { DeliveryServiceCard } from "../../ui/Delivery";
import { IntegrationFilters, FeatureGate } from "../../ui";
import { SUBSCRIPTION_FEATURES } from "../../../lib/subscriptionFeatures";

const DeliveryServicesPage = ({ selectedBrand }) => {
  const { t } = useTranslation("delivery");
  const { data, isLoading, error } = useDriverCompanies();
  const [searchTerm, setSearchTerm] = useState("");

  const driverCompanies = data?.driverCompanies || [];

  // Filter driver companies locally based on search term
  const filteredDriverCompanies = useMemo(() => {
    if (!searchTerm.trim()) {
      return driverCompanies;
    }

    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    return driverCompanies.filter((company) =>
      company.name?.toLowerCase().includes(lowerSearchTerm),
    );
  }, [driverCompanies, searchTerm]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  return (
    <FeatureGate
      featureKey={SUBSCRIPTION_FEATURES.INTEGRATIONS_DELIVERY_SERVICES}
      featureName={t("services.header.title")}
    >
      <div>
        <PageHeader
          title={t("services.header.title")}
          subtitle={t("services.header.subtitle")}
          selectedBrand={selectedBrand}
        />

        {/* Filters */}
        {!isLoading && !error && (
          <IntegrationFilters
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            placeholder={t("services.searchPlaceholder")}
          />
        )}

        <div className="mt-6">
          {isLoading ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="animate-pulse rounded-lg border border-gray-800 bg-gray-900 p-6"
                >
                  <div className="mb-4 flex items-start space-x-3">
                    <div className="h-16 w-16 rounded-lg bg-gray-800"></div>
                    <div className="flex-1">
                      <div className="mb-2 h-5 w-3/4 rounded bg-gray-800"></div>
                      <div className="h-4 w-1/2 rounded bg-gray-800"></div>
                    </div>
                  </div>
                  <div className="border-t border-gray-800 pt-4">
                    <div className="h-9 rounded bg-gray-800"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="rounded-lg border border-red-500/30 bg-red-900/20 p-6">
              <div className="flex items-center space-x-3">
                <div className="text-red-500">
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-red-400">
                    {t("services.error.title")}
                  </h3>
                  <p className="mt-1 text-sm text-red-300">
                    {error.message || t("services.error.defaultMessage")}
                  </p>
                  <button
                    onClick={() => window.location.reload()}
                    className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
                  >
                    {t("services.actions.retry")}
                  </button>
                </div>
              </div>
            </div>
          ) : filteredDriverCompanies.length === 0 ? (
            <div className="py-12 text-center">
              <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-gray-800">
                <svg
                  className="h-12 w-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m-4 0v1a1 1 0 001 1h2m-4-3h16m0 3a2 2 0 11-4 0m4 0h2a1 1 0 001-1v-1m-6 0a2 2 0 104 0"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-lg font-medium text-white">
                {searchTerm
                  ? t("services.empty.searchTitle")
                  : t("services.empty.defaultTitle")}
              </h3>
              <p className="text-gray-400">
                {searchTerm
                  ? t("services.empty.searchDescription", { searchTerm })
                  : t("services.empty.defaultDescription")}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {filteredDriverCompanies.map((driverCompany) => (
                <DeliveryServiceCard
                  key={driverCompany.id}
                  driverCompany={driverCompany}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </FeatureGate>
  );
};

export default DeliveryServicesPage;
